import React from 'react';
import { FieldRow } from '../../ui/FieldRow';
import { NumberField } from '../../ui/NumberField';
import { PillButton } from '../../ui/PillButton';
export type ManualTool = 'create' | 'select';
type ManualPlacementProps = {
  tool: ManualTool;
  onToolChange: (tool: ManualTool) => void;
  brushRadius: number;
  onBrushRadiusChange: (v: number) => void;
  spacing: number;
  onSpacingChange: (v: number) => void;
  onSupportAllMinima: () => void;
};
function CreateIcon() {
  const dots: {
    x: number;
    y: number;
    r: number;
  }[] = [];
  for (let i = -3; i <= 3; i++) {
    for (let j = -3; j <= 3; j++) {
      const d = Math.hypot(i, j);
      if (d > 3.2) continue;
      dots.push({
        x: 12 + i * 3,
        y: 12 + j * 3,
        r: d < 1.5 ? 1.25 : d < 2.5 ? 0.95 : 0.6
      });
    }
  }
  return <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="currentColor" aria-hidden="true" data-id="element-494" data-mp="8tx2456fdix9">
      {dots.map((d, i) => <circle key={i} cx={d.x} cy={d.y} r={d.r} data-id="element-495" data-mp="3vva2ur0u99n" data-mpi={i} />)}
    </svg>;
}
function SelectIcon() {
  return <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="2.4 2" aria-hidden="true" data-id="element-496" data-mp="km7sxvgiqmnw">
      <rect x="3.5" y="3.5" width="17" height="17" data-id="element-497" data-mp="tluoi0mkgy91" />
    </svg>;
}
const tools: {
  id: ManualTool;
  label: string;
  Icon: () => JSX.Element;
}[] = [{
  id: 'create',
  label: 'Create',
  Icon: CreateIcon
}, {
  id: 'select',
  label: 'Select & Edit',
  Icon: SelectIcon
}];
export function ManualPlacement({
  tool,
  onToolChange,
  brushRadius,
  onBrushRadiusChange,
  spacing,
  onSpacingChange,
  onSupportAllMinima
}: ManualPlacementProps) {
  return <div className="mt-[6px] rounded-[4px] border border-pf-line bg-pf-panel px-[8px] pb-[8px] pt-[8px]" data-id="element-498" data-mp="1db51v7yub73z">
      <div role="radiogroup" aria-label="Manual placement tool" className="grid grid-cols-2 gap-[10px]" data-id="element-499" data-mp="1ha5kapdeo7qg">
        {tools.map(({
        id,
        label,
        Icon
      }) => {
        const active = tool === id;
        return <button key={id} type="button" role="radio" aria-checked={active} onClick={() => onToolChange(id)} className={`flex h-[78px] flex-col items-center justify-center gap-[8px] rounded-[6px] border text-[12.5px] transition-colors duration-150 ${active ? 'border-pf-blue bg-pf-blue-soft text-pf-blue' : 'border-pf-line bg-white text-pf-muted hover:bg-[#fafafa]'}`} data-id="element-500" data-mp="1r9wdmnq8cgoy">
              
              <span className={active ? 'text-pf-blue' : 'text-[#4b5056]'} data-id="element-501" data-mp="9w1tvl5sfo5r">
                <Icon data-id="element-502" data-mp="5vhueaxqs8gg" />
              </span>
              {label}
            </button>;
      })}
      </div>

      <FieldRow label="Touchpoint Size" info className="mt-[8px] min-h-[36px]" data-id="element-503" data-mp="jviwftpqtlxi">
        <NumberField ariaLabel="Touchpoint size" value={0.5} unit="mm" step={0.05} className="h-[30px] w-[124px]" data-id="element-504" data-mp="exzkx1kfjn99" />
      </FieldRow>

      <FieldRow label="Brush Radius" info className="min-h-[36px]" data-id="element-505" data-mp="2lnntfjm3n9f3">
        <NumberField ariaLabel="Brush radius" value={brushRadius} onChange={onBrushRadiusChange} decimals={1} unit="mm" min={1} max={10} className="h-[30px] w-[124px]" data-id="element-506" data-mp="2gpcjl8vt0ywv" />
      </FieldRow>
      <input aria-label="Brush radius slider" type="range" className="pf-range my-[4px] px-[6px]" min={1} max={10} step={0.1} value={brushRadius} onChange={e => onBrushRadiusChange(Number(e.target.value))} data-id="element-507" data-mp="115mtmvr58sa4" />
      

      <FieldRow label="Spacing" info className="mt-[2px] min-h-[36px]" data-id="element-508" data-mp="dg2bgf2tdz1w">
        <NumberField ariaLabel="Spacing" value={spacing} onChange={onSpacingChange} decimals={1} unit="mm" min={1} max={8} className="h-[30px] w-[124px]" data-id="element-509" data-mp="di599la7kwh6" />
      </FieldRow>
      <input aria-label="Spacing slider" type="range" className="pf-range my-[4px] px-[6px]" min={1} max={8} step={0.1} value={spacing} onChange={e => onSpacingChange(Number(e.target.value))} data-id="element-510" data-mp="3j5y9w1z0or0" />
      

      <PillButton variant="outlineAccent" className="mt-[6px] h-[30px] w-full text-[13px]" onClick={onSupportAllMinima} data-id="element-511" data-mp="22uz0c73m8kal">
        Support All Minima
      </PillButton>
    </div>;
}